import React, { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsContent } from '@/components/ui/tabs';
import { useAuth } from '@/auth';
import { checkAnyPermissions, PERMISSIONS } from '@/auth/permissions';
import { createTabsConfig } from './tabsConfig';
import { getFilteredTabs, getDefaultTab, getActiveCount, getTotalCount } from './helpers';
import TabTrigger from './components/TabTrigger';
import TabContent from './components/TabContent';
import EmptyState from './components/EmptyState';

interface TramiteTabsProps {
  tramite: any;
  defaultTab?: string;
}

const TramiteTabs: React.FC<TramiteTabsProps> = ({
  tramite,
  defaultTab = 'comentarios'
}) => {
  const { user } = useAuth();
  const userPermissions = user?.permissions || [];
  const canManageTramites = checkAnyPermissions(userPermissions, [
    PERMISSIONS.MANAGE_TRAMITES.name,
  ]);

  const tabsConfig = createTabsConfig(userPermissions, user, tramite);
  const visibleTabs = getFilteredTabs(tabsConfig, canManageTramites, tramite);
  const actualDefaultTab = getDefaultTab(defaultTab, visibleTabs, canManageTramites);

  // Hook para manejar la persistencia de la tab activa
  const usePersistedTabState = (tramiteId: number, defaultTab: string) => {
    const storageKey = `tramite_active_tab_${tramiteId}`;

    const [activeTab, setActiveTab] = useState(() => {
      try {
        const stored = localStorage.getItem(storageKey);
        return stored || defaultTab;
      } catch {
        return defaultTab;
      }
    });

    const handleTabChange = (newTab: string) => {
      setActiveTab(newTab);
      try {
        localStorage.setItem(storageKey, newTab);
      } catch {
        // Silently fail if localStorage is not available
      }
    };

    return [activeTab, handleTabChange] as const;
  };

  const [activeTab, setActiveTab] = usePersistedTabState(tramite.id, actualDefaultTab);

  // Validar que la tab activa esté disponible en las tabs visibles
  useEffect(() => {
    const isTabAvailable = visibleTabs.some(tab => tab.id === activeTab);
    if (!isTabAvailable && visibleTabs.length > 0) {
      setActiveTab(actualDefaultTab);
    }
  }, [visibleTabs, activeTab, actualDefaultTab, setActiveTab]);

  if (visibleTabs.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="w-full my-6 sm:my-8">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <TabsList className={`
            grid sm:flex sm:flex-wrap
            ${visibleTabs.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}
            gap-1 sm:items-center sm:justify-start
            rounded-md bg-muted p-1 text-muted-foreground w-full
            h-auto
          `}>
            {visibleTabs.map((tab) => {
              const activeCount = getActiveCount(tab.items, tab, tramite);
              const totalCount = getTotalCount(tab.items, tab, tramite);

              return (
                <TabTrigger
                  key={tab.id}
                  tab={tab}
                  activeCount={activeCount}
                  totalCount={totalCount}
                  canManageTramites={canManageTramites}
                />
              );
            })}
          </TabsList>
        </div>

        {visibleTabs.map((tab) => (
          <TabsContent key={tab.id} value={tab.id} className="mt-0 pt-0">
            <TabContent
              tab={tab}
              tramite={tramite}
              canManageTramites={canManageTramites}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default TramiteTabs;